import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Types ────────────────────────────────────────────────────────────────────

export type ScheduleOption = '15min' | '30min' | '60min';
export type StrategyId = 'custom' | 'auto-directional' | 'momentum' | 'oversold-bounce' | 'breakout' | 'trend-following' | 'bearish-momentum' | 'overbought-reversal' | 'breakdown' | 'bearish-trend-following';
export type DirectionOption = 'long' | 'short' | 'auto';
export type RiskAppetite = 'conservative' | 'moderate' | 'aggressive' | 'very-aggressive' | 'max-risk';
export type EngineStatus = 'active' | 'paused' | 'error';
export type SignalAction = 'BUY' | 'SELL' | 'HOLD';
export type SignalStatus = 'pending' | 'submitted' | 'executed' | 'dismissed';
export type TradeStatus = 'submitted' | 'open' | 'closed' | 'cancelled';
export type AuditStatus = 'success' | 'failure' | 'skip' | 'warning';
export type AiMode = 'off' | 'advisory' | 'strict';
export type AuditCategory = 'system' | 'trade_lifecycle' | 'signal_generation' | 'agent_workflow';

export interface ExecutionRules {
  cashReservePct: number;
  maxPositionPct: number;
  maxOpenTrades: number;
  maxDailyTrades: number;
  maxDrawdownPct?: number;
  maxDailyDrawdownPct?: number;
  maxWeeklyDrawdownPct?: number;
  scaledEntry?: boolean;
  entryTranches?: number;
  trailingStopEnabled?: boolean;
  profitProtectionEnabled?: boolean;
  washSaleProtection?: boolean;
}

export interface ConditionOverride {
  indicator: string;
  operator: 'gt' | 'gte' | 'lt' | 'lte' | 'between';
  value?: number;
  min?: number;
  max?: number;
  weight: number;
  label: string;
}

export interface AlgoPolicy {
  policyId: string;
  name: string;
  strategyId: StrategyId;
  symbols: string[];
  riskAppetite: RiskAppetite;
  enabled: boolean;
  createdAt: string;
  defaultQty?: number | null;
  direction?: DirectionOption;
  conditionOverrides?: ConditionOverride[];
}

export interface AlgoTradePreview {
  preview: true;
  equity: number;
  buyingPower: number;
  suggestedQty: number;
  maxQty: number;
  entryPrice: number;
  symbol: string;
  riskAppetite: RiskAppetite;
  positionSizePct: number;
  policyDefaultQty: number | null;
  orderType: 'limit' | 'bracket';
  suggestedStop?: number;
  suggestedTarget?: number;
}

export interface AlgoEngine {
  engineId: string;
  name: string;
  schedule: ScheduleOption;
  status: EngineStatus;
  alpacaEnvironment: 'paper' | 'live';
  policies: AlgoPolicy[];
  executionRules?: ExecutionRules;
  aiMode?: AiMode;
  claudeEnabled?: boolean; // legacy, mirrors aiMode !== 'off'
  autoExecuteEnabled?: boolean;
  autoExecuteThreshold?: number;
  createdAt: string;
  updatedAt: string;
  lastRunAt: string | null;
  signalSummary: {
    lastSignalAt: string | null;
    lastSignalSymbol: string | null;
    lastSignalAction: string | null;
    pendingSignals: number;
  };
}

export interface AlgoSentiment {
  score: number;
  label: string;
  confidence: number;
  catalyst: string | null;
  headlines: Array<{ title: string; publisher: string }>;
  sources_count: number;
}

export interface ClaudeAnalysis {
  confidence: number;
  recommendation: 'EXECUTE' | 'HOLD' | 'SKIP';
  reasoning: string;
  keyRisks: string[];
  marketContext: string;
  sizingAdjustment: number;
  model: string;
  analyzedAt: string;
}

export interface AlgoSignal {
  userId: string;
  signalId: string;
  engineId: string;
  policyId: string;
  symbol: string;
  strategyId: StrategyId;
  action: SignalAction;
  direction?: 'long' | 'short';
  score: number;
  rawScore?: number;
  rationale: string;
  suggestedEntry: number;
  suggestedStop: number;
  suggestedTarget: number;
  riskAppetite: RiskAppetite;
  status: SignalStatus;
  sentiment?: AlgoSentiment | null;
  indicators: {
    rsi: number | null;
    volumeRatio: number;
    bbPosition: number;
    macdHistogram: number | null;
    sma20: number | null;
    sma50: number | null;
    atr: number | null;
    avwap: number | null;
    avwapDistance: number | null;
  };
  currentPrice?: number | null;
  lastCheckedAt?: string | null;
  claudeAnalysis?: ClaudeAnalysis | null;
  autoExecuted?: boolean;
  autoExecuteReason?: string | null;
  createdAt: string;
  trade?: AlgoTrade | null;
}

export interface AlgoTrade {
  userId: string;
  tradeId: string;
  engineId: string;
  policyId: string;
  signalId: string;
  symbol: string;
  side: 'buy' | 'sell';
  qty: number;
  filledPrice: number | null;
  alpacaOrderId: string;
  alpacaOrderStatus?: string;
  status: TradeStatus;
  entryAt: string;
  exitAt: string | null;
  pnl: number | null;
  exitPrice?: number | null;
  exitReason?: string | null;
  suggestedStop?: number;
  suggestedTarget?: number;
  currentPrice?: number | null;
  unrealizedPnl?: number | null;
  lastCheckedAt?: string | null;
  orderType?: 'limit' | 'bracket';
  limitPrice?: number;
  bracketLegs?: { takeProfit: number; stopLoss: number } | null;
  priceHistory?: Array<{ t: string; p: number }>;
  closeFailedAt?: string | null;
  closeFailedReason?: string | null;
}

export interface AuditEvent {
  userId: string;
  eventId: string;
  runId: string | null;
  engineId: string | null;
  sequence: number;
  eventType: string;
  category: AuditCategory;
  status: AuditStatus;
  symbol: string | null;
  policyId: string | null;
  summary: string | null;
  detail: Record<string, unknown> | null;
  durationMs: number | null;
  timestamp: string;
}

export interface AuditRunSummary {
  runId: string;
  engineId: string;
  startedAt: string | null;
  endedAt: string | null;
  totalEvents: number;
  symbolsProcessed: string[];
  signalsCreated: number;
  tradesExecuted: number;
  errors: number;
  status: 'success' | 'error';
}

export interface CreateEngineRequest {
  name: string;
  schedule: ScheduleOption;
  alpacaApiKey: string;
  alpacaSecretKey: string;
  alpacaEnvironment: 'paper' | 'live';
  policies: Array<{
    name: string;
    strategyId: StrategyId;
    direction?: DirectionOption;
    symbols: string[];
    riskAppetite: RiskAppetite;
    conditionOverrides?: ConditionOverride[];
  }>;
  executionRules?: Partial<ExecutionRules>;
  aiMode?: AiMode;
  claudeEnabled?: boolean; // legacy
  autoExecuteEnabled?: boolean;
  autoExecuteThreshold?: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const algoService = {
  // Engines
  async getEngines(token: string): Promise<{ engines: AlgoEngine[]; total: number }> {
    // Alpaca-only read — separate from /algo/engines (which is now legacy/TT)
    const res = await api.get('/alpaca/engines', { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  },

  async createEngine(token: string, payload: CreateEngineRequest): Promise<{ engine: AlgoEngine }> {
    const res = await api.post('/algo/engines', payload, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  },

  async updateEngine(
    token: string,
    engineId: string,
    updates: { name?: string; schedule?: ScheduleOption; status?: EngineStatus; policies?: AlgoPolicy[]; executionRules?: Partial<ExecutionRules>; aiMode?: AiMode; claudeEnabled?: boolean; autoExecuteEnabled?: boolean; autoExecuteThreshold?: number }
  ): Promise<void> {
    await api.put(`/algo/engines/${engineId}`, updates, { headers: { Authorization: `Bearer ${token}` } });
  },

  async deleteEngine(token: string, engineId: string): Promise<void> {
    await api.delete(`/algo/engines/${engineId}`, { headers: { Authorization: `Bearer ${token}` } });
  },

  // Signals
  async getSignals(
    token: string,
    options?: { engineId?: string; status?: SignalStatus; limit?: number }
  ): Promise<{ signals: AlgoSignal[]; total: number; hasMore: boolean }> {
    const params = new URLSearchParams();
    if (options?.engineId) params.append('engineId', options.engineId);
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', String(options.limit));
    const query = params.toString() ? `?${params}` : '';
    const res = await api.get(`/alpaca/signals${query}`, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  },

  // Preview trade — returns account info + suggested qty without placing order
  async previewTrade(token: string, signalId: string): Promise<AlgoTradePreview> {
    const sid = encodeURIComponent(signalId);
    const res = await api.post(
      `/algo/signals/${sid}/execute`,
      { preview: true },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
  },

  // Execute trade — optionally with a user-specified qty
  async executeTrade(
    token: string,
    signalId: string,
    qty?: number
  ): Promise<{ trade: { tradeId: string; symbol: string; side: string; qty: number; alpacaOrderId: string; status: string } }> {
    const sid = encodeURIComponent(signalId);
    const body: Record<string, unknown> = {};
    if (qty && qty > 0) body.qty = qty;
    const res = await api.post(
      `/algo/signals/${sid}/execute`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
  },

  // Dismiss signal
  async dismissSignal(token: string, signalId: string): Promise<void> {
    const sid = encodeURIComponent(signalId);
    await api.post(
      `/algo/signals/${sid}/execute`,
      { action: 'dismiss' },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },

  // Trades (reuse signals query with trade data attached)
  async getTrades(
    token: string,
    options?: { engineId?: string; limit?: number }
  ): Promise<{ signals: AlgoSignal[]; total: number; hasMore: boolean }> {
    return algoService.getSignals(token, { ...options, status: 'executed' });
  },

  // Audit log
  async getAuditRuns(
    token: string,
    options?: { engineId?: string; startDate?: string; endDate?: string; limit?: number; source?: string }
  ): Promise<{ runs: AuditRunSummary[]; events: AuditEvent[] }> {
    const params = new URLSearchParams();
    if (options?.source) params.append('source', options.source);
    if (options?.engineId) params.append('engineId', options.engineId);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.limit) params.append('limit', String(options.limit));
    const query = `?${params}`;
    const res = await api.get(`/alpaca/audit${query}`, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  },

  async getAuditRunEvents(token: string, runId: string): Promise<{ events: AuditEvent[] }> {
    const res = await api.get(`/alpaca/audit?runId=${encodeURIComponent(runId)}`, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  },
};

// ─── Strategy metadata ────────────────────────────────────────────────────────

export const STRATEGIES: Record<StrategyId, { name: string; description: string; icon: string }> = {
  'custom': {
    name: 'Custom Strategy',
    description: 'Build your own strategy with custom entry conditions using technicals, fundamentals, and market data',
    icon: '⚙',
  },
  'auto-directional': {
    name: 'Auto Directional',
    description: 'Automatically trades both long and short — evaluates all 8 strategies, picks the best direction per stock based on technicals, sentiment, and market regime',
    icon: '⇅',
  },
  'momentum': {
    name: 'Momentum',
    description: 'RSI momentum + volume surge + price above SMA20',
    icon: '⚡',
  },
  'oversold-bounce': {
    name: 'Oversold Bounce',
    description: 'RSI < 30 oversold reversal + Bollinger Band lower breach',
    icon: '↩',
  },
  'breakout': {
    name: 'Breakout',
    description: 'Price breaking above 20-day high with volume confirmation',
    icon: '↑',
  },
  'trend-following': {
    name: 'Trend Following',
    description: 'SMA20 > SMA50 crossover with MACD confirmation',
    icon: '~',
  },
  'bearish-momentum': {
    name: 'Bearish Momentum',
    description: 'RSI declining + volume surge + price below SMA20 (SHORT)',
    icon: '⬇',
  },
  'overbought-reversal': {
    name: 'Overbought Reversal',
    description: 'RSI > 68 at upper Bollinger Band + MACD fading (SHORT)',
    icon: '↪',
  },
  'breakdown': {
    name: 'Breakdown',
    description: 'Price breaking 20-day low with heavy volume (SHORT)',
    icon: '↓',
  },
  'bearish-trend-following': {
    name: 'Bearish Trend Following',
    description: 'SMA20 < SMA50 death cross with MACD confirmation (SHORT)',
    icon: '⤵',
  },
};

export const RISK_LABELS: Record<RiskAppetite, { label: string; color: string; description: string }> = {
  conservative:      { label: 'Conservative',      color: '#0d9488', description: '2% position, min score 75' },
  moderate:          { label: 'Moderate',          color: '#d97706', description: '5% position, min score 65' },
  aggressive:        { label: 'Aggressive',        color: '#dc2626', description: '10% position, min score 55' },
  'very-aggressive': { label: 'Very Aggressive',   color: '#be123c', description: '15% position, min score 40' },
  'max-risk':        { label: 'Max Risk',          color: '#7f1d1d', description: '20% position, min score 25 — use with caution' },
};

export const SCHEDULE_LABELS: Record<ScheduleOption, string> = {
  '15min': 'Every 15 minutes',
  '30min': 'Every 30 minutes',
  '60min': 'Every hour',
};
